import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import { Modal, PaginationArrow, SingleSelect } from '@/common';
import { UploadButton } from '@/components/common';
import { Search } from '@/components/common/Search';
import { Button, Label, LABEL_TYPE, Spinner } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import {
  AUDIO,
  IMAGE,
  PAGE_SIZE,
  STOCK_CLIP,
  TEXT_OVERLAY,
  UPLOAD_PICKER_TYPES,
  VIDEO
} from '@/constants';
import { RolesAndPermissionContext } from '@/contexts';
import { AssetScope, AssetType, COLORS, PERMISSION_TYPE } from '@/enums';
import {
  filterAssetsByMultipleTypes,
  filterAssetsByType,
  getAssetsTotalDuration,
  getCombinedVideosDuration,
  getDurationOfAssets,
  getPhotoAssetFilterOptions,
  getRemainingDuration,
  showToast,
  ToastType
} from '@/helpers';
import { useFileUpload } from '@/hooks';
import { AssetItem } from '@/pages/Event/Asset/AssetItem';
import { AssetsTagFilter } from '@/pages/Event/Asset/AssetTagFilter';
import { AssetTagItem } from '@/pages/Event/Asset/AssetTagItem';
import { DroppedAssetPopup } from '@/pages/Event/Asset/DroppedAssetPopup';
import { ImageBlueBoldIcon as ImageIcon, PhotoFilmIcon } from '@/resources';
import { useGetEventAssets, useGetEventTags } from '@/services';
import { useGetStockClips } from '@/services/stockClip.service';
import { getTestId, TestIds } from '@/shared/test-ids';
import { Asset, SelectOptionProps, Tag, ThemeProp, TributeVideoAsset } from '@/types';
import { StockClipAsset } from '@/types/StockClipTypes';

type PhotosProps = {
  theme: ThemeProp;
  id: string;
  setEnableNext: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedPhotos: React.Dispatch<React.SetStateAction<TributeVideoAsset[]>>;
  selectedPhotos: TributeVideoAsset[];
  assets: TributeVideoAsset[];
  maxAssets?: number;
  minAssets: number;
  durationOfSingleImage?: number;
  organizationId?: number;
};

type AssetWithOrgIndex = Asset & { originalIndex: number };

export const Photos = ({
  theme,
  id,
  setEnableNext,
  selectedPhotos,
  setSelectedPhotos,
  assets,
  maxAssets,
  minAssets,
  durationOfSingleImage,
  organizationId
}: PhotosProps) => {
  const [t] = useTranslation('common');
  const { isAllowedTo } = useContext(RolesAndPermissionContext);
  const { ref, inView } = useInView();
  const [selectedAsset, setSelectedAsset] = useState<Asset>();
  const [selectedAssets, setSelectedAssets] = useState<Asset[]>([]);
  const [isUploadedPhotosModalOpen, setIsUploadedPhotosModalOpen] = useState<boolean>(false);
  const [maximumPhotosSelected, setMaximumPhotosSelected] = useState<boolean>(false);
  const [buttonsAreDisabled, setButtonsAreDisabled] = useState<boolean>(false);
  const [filterByType, setFilterByType] = useState<SelectOptionProps>();
  const [selectedTags, setSelectedTags] = useState<SelectOptionProps[]>();
  const [lastClickedAsset, setLastClickedAsset] = useState<AssetWithOrgIndex | null>(null);
  const [isStockLibrary, setIsStockLibrary] = useState<boolean>(false);
  const [search, setSearch] = useState<string>();
  const [dragging, setDragging] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const [showRecommendedVideos, setShowRecommendedVideos] = useState(true);
  const previousDurationOfAssetsRef = useRef<number | null>(null);
  const previousDurationOfRemaining = useRef<number | null>(null);

  const photos = useMemo(() => filterAssetsByType(selectedPhotos, IMAGE), [selectedPhotos]);
  const textOverlays = useMemo(
    () => filterAssetsByType(selectedPhotos, TEXT_OVERLAY),
    [selectedPhotos]
  );
  const videos = useMemo(
    () => filterAssetsByMultipleTypes(selectedPhotos, [VIDEO, STOCK_CLIP]),
    [selectedPhotos]
  );
  const audio = useMemo(() => filterAssetsByType(assets, AUDIO), [assets]);
  const totalDuration = useMemo(() => getAssetsTotalDuration(audio), [audio]);
  const combinedVideosDuration = useMemo(() => getCombinedVideosDuration(videos), [videos]);
  const remainingDuration = useMemo(
    () => getRemainingDuration(totalDuration, theme?.duration ?? 0, combinedVideosDuration),
    [totalDuration, combinedVideosDuration]
  );

  const durationOfAssets = useMemo(
    () => getDurationOfAssets(photos, textOverlays, remainingDuration),
    [photos, textOverlays, remainingDuration]
  );
  const hasNoPhotosOrTextOverlays = photos.length === 0 && textOverlays.length === 0;
  const hasAnyVisualAssets = selectedPhotos.length > 0;
  const availableDurationForDisplay = Math.max(
    0,
    Math.round(totalDuration - (theme?.duration ?? 0))
  );
  const videosDurationForDisplay = Math.max(0, Math.round(combinedVideosDuration));
  const invalidAveragePhotoDuration = durationOfSingleImage === undefined && durationOfAssets < 5;
  const invalidTotalVideoDuration = durationOfSingleImage === undefined && remainingDuration < 0;

  const { data: allTags } = useGetEventTags(id);

  const {
    data: publicAssetsData,
    fetchNextPage: publicAssetsFetchNextPage,
    isFetchingNextPage: isPublicAssetsFetchingNextPage,
    hasNextPage: publicAssetsHasNextPage
  } = useGetStockClips(PAGE_SIZE, undefined, search, undefined, false, 'StockClipName');

  const { data: recommendedStockClipsData } = useGetStockClips(
    PAGE_SIZE,
    undefined,
    search,
    theme?.id,
    false,
    'StockClipName'
  );

  const hasRecommendedStockClips = recommendedStockClipsData?.pages?.some(
    (page) => page.data?.items.length > 0
  );

  const {
    isLoading,
    data: allAssets,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = useGetEventAssets(
    id,
    selectedTags?.map((tag: SelectOptionProps) => tag?.value as string) ?? [],
    PAGE_SIZE,
    undefined,
    filterByType
      ? ([filterByType?.value] as string[])
      : [AssetType.IMAGE.toString(), AssetType.VIDEO.toString(), AssetType.TEXT_OVERLAY.toString()],
    undefined,
    true
  );

  const videoNumber = allAssets?.pages.map((page) =>
    page.data?.items?.filter((item: Asset) => item.type === AssetType.TRIBUTE_VIDEO)
  ).length;

  const tributeVideoTag = allTags?.data?.find(
    (tag: Tag) => tag.name === `Tribute Video ${videoNumber ?? 0 + 1}`
  );
  const handleUploadedAssets = (asset: Asset) => {
    setSelectedPhotos((prevSelectedPhotos) => [
      ...prevSelectedPhotos,
      {
        id: 0,
        tributeVideoId: 0,
        asset: asset,
        order: Math.max(...prevSelectedPhotos.map((photo) => photo.order), 0) + 1,
        durationMultiplier: 1
      }
    ]);
  };
  const { uploadFiles, isUploading: isUploadingAssets } = useFileUpload({
    eventId: id,
    organizationId,
    pickerTypes: UPLOAD_PICKER_TYPES.IMAGE_VIDEO,
    isPublic: false,
    assetScope: AssetScope.PRIVATE,
    assetTag: tributeVideoTag?.name ?? `Tribute Video ${videoNumber ?? 0 + 1}`,
    maxFiles: maxAssets ? maxAssets - selectedPhotos.length : undefined,
    onCreatedAsset: handleUploadedAssets
  });

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    } else if (inView && publicAssetsHasNextPage) {
      publicAssetsFetchNextPage();
    }

    const handleMouseDown = (event: MouseEvent) => {
      if (event.shiftKey) {
        event.preventDefault();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [inView]);

  useEffect(() => {
    if (isUploadedPhotosModalOpen) {
      setLastClickedAsset(null);
      setShowRecommendedVideos(true);
    }
  }, [isUploadedPhotosModalOpen]);

  const onSubmitSelection = () => {
    if ((maxAssets && selectedAssets && selectedAssets.length <= maxAssets) || !maxAssets) {
      selectedAssets.forEach((asset) => {
        setSelectedPhotos((prevSelectedPhotos) => [
          ...prevSelectedPhotos,
          {
            id: 0,
            tributeVideoId: 0,
            asset: asset,
            order: prevSelectedPhotos.length + 1,
            durationMultiplier: 1
          }
        ]);
      });
    }
    setIsUploadedPhotosModalOpen(false);
    setMaximumPhotosSelected(false);
    setSelectedAssets([]);
  };

  useEffect(() => {
    const isDurationValidationEnabled = durationOfSingleImage === undefined;
    const hasValidDuration =
      !isDurationValidationEnabled || (durationOfAssets >= 5 && remainingDuration >= 0);
    const hasInvalidDuration = isDurationValidationEnabled && !hasValidDuration;
    const hasMaxAssetsLimit = Boolean(maxAssets);
    const maxAssetsValue = maxAssets ?? 0;
    const hasReachedMaxAssets =
      hasMaxAssetsLimit && selectedPhotos.length + selectedAssets.length >= maxAssetsValue;

    setEnableNext(!isUploadingAssets && selectedPhotos.length >= minAssets && hasValidDuration);

    setButtonsAreDisabled(hasMaxAssetsLimit ? hasReachedMaxAssets || hasInvalidDuration : false);
  }, [
    selectedPhotos,
    selectedAssets,
    minAssets,
    maxAssets,
    isUploadingAssets,
    durationOfAssets,
    remainingDuration
  ]);

  useEffect(() => {
    const previousDuration = previousDurationOfAssetsRef.current;
    const previousRemainingDuration = previousDurationOfRemaining.current;

    if (invalidAveragePhotoDuration && (previousDuration === null || previousDuration >= 5)) {
      showToast(ToastType.WARNING, t('averagePhotoDurationShouldBeAtLeast5Seconds'));
    }
    if (
      invalidTotalVideoDuration &&
      (previousRemainingDuration === null || previousRemainingDuration >= 0)
    ) {
      showToast(
        ToastType.WARNING,
        t('videoContentExceedsAllowedDuration', {
          allowedDuration: availableDurationForDisplay,
          currentDuration: Math.max(0, Math.round(combinedVideosDuration))
        })
      );
    }

    previousDurationOfRemaining.current = remainingDuration;
    previousDurationOfAssetsRef.current = durationOfAssets;
  }, [durationOfAssets, remainingDuration]);

  useEffect(() => {
    setMaximumPhotosSelected(
      maxAssets ? selectedPhotos.length + selectedAssets.length > maxAssets : false
    );
  }, [selectedPhotos, selectedAssets, maxAssets]);

  const handleUpdateAssetOrder = (currentOrder: number, targetedOrder: number) => {
    const updatedArray = selectedPhotos?.map((obj) => {
      if (currentOrder < targetedOrder) {
        if (obj.order > currentOrder && obj.order <= targetedOrder) {
          return { ...obj, order: obj.order - 1 };
        }
      } else if (currentOrder > targetedOrder) {
        if (obj.order < currentOrder && obj.order >= targetedOrder) {
          return { ...obj, order: obj.order + 1 };
        }
      }
      if (obj.order === currentOrder) {
        return { ...obj, order: targetedOrder };
      }
      return obj;
    });

    const reorderedArray = updatedArray
      .sort((a, b) => a.order - b.order)
      .map((obj, index) => ({ ...obj, order: index + 1 }));

    setSelectedPhotos(reorderedArray);
  };

  const globalIndex = (pageIndex: number, index: number) => {
    return pageIndex * PAGE_SIZE + index;
  };

  const handleClickAsset = (asset: Asset, event: React.MouseEvent, index: number) => {
    event.preventDefault();

    const allAssetsInPages =
      (isStockLibrary ? publicAssetsData : allAssets)?.pages.flatMap((page, pageIndex) =>
        isStockLibrary
          ? page.data?.items.map((item: { stockClipAsset: StockClipAsset }, idx: number) => {
              return {
                ...(item.stockClipAsset ?? item),
                originalIndex: globalIndex(pageIndex, idx)
              };
            })
          : page.data?.items.map((item: { stockClipAsset: StockClipAsset }, idx: number) => {
              return {
                ...item,
                originalIndex: globalIndex(pageIndex, idx)
              };
            })
      ) || [];
    let newSelectedAssets = [...selectedAssets];
    if (event.shiftKey && lastClickedAsset) {
      const start = Math.min(index, lastClickedAsset.originalIndex);
      const end = Math.max(index, lastClickedAsset.originalIndex);

      const rangeAssets = allAssetsInPages.filter(
        (a) => a.originalIndex >= start && a.originalIndex <= end
      );

      const uniqueAssets = new Map();
      [...selectedAssets, ...rangeAssets].forEach((asset) => uniqueAssets.set(asset.id, asset));

      const uniqueAssetsSorted = Array.from(uniqueAssets.values()).sort(
        (a, b) => a.originalIndex - b.originalIndex
      );

      newSelectedAssets = Array.from(uniqueAssetsSorted.values());
    } else {
      const assetIndex = selectedAssets.findIndex((selectedAsset) => selectedAsset.id === asset.id);

      if (assetIndex === -1) {
        newSelectedAssets.push(asset);
      } else {
        newSelectedAssets = selectedAssets?.filter(
          (selectedAsset) => selectedAsset.id !== asset.id
        );
      }
    }
    const clickedWithGlobalIndex = allAssetsInPages.find((a) => a.id === asset.id);
    const originalIndex = clickedWithGlobalIndex?.originalIndex ?? index;
    setSelectedAssets(newSelectedAssets);
    setLastClickedAsset({ ...asset, originalIndex: originalIndex });
  };

  const handleTagRemove = (tag: SelectOptionProps) => {
    if (selectedTags?.includes(tag)) {
      setSelectedTags(selectedTags?.filter((t) => t?.value !== tag?.value));
    }
  };

  const isAssetItemDragged = (event: React.DragEvent<HTMLDivElement>) => {
    if (
      event.dataTransfer &&
      (event.dataTransfer.types.includes('application/slotjson') ||
        event.dataTransfer.types.includes('application/slotJson') ||
        event.dataTransfer.types.includes('text/plain') ||
        event.dataTransfer.types.includes('text/html') ||
        event.dataTransfer.types.includes('text/uri-list'))
    ) {
      return true;
    }
    return false;
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0 && !isAssetItemDragged(e)) {
      setDroppedFiles(files);
      setShowPopup(true);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(!isAssetItemDragged(e));
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setDragging(false);
    }
  };

  const handleCancel = () => {
    setShowPopup(false);
    setDroppedFiles([]);
  };

  const handleCreatedAsset = (createdAsset: Asset) => {
    setSelectedPhotos((prevAssets) => {
      const newAsset = {
        id: 0,
        tributeVideoId: 0,
        asset: {
          id: createdAsset.id,
          assetName: createdAsset.assetName,
          assetScope: createdAsset.assetScope,
          assetSize: createdAsset.assetSize,
          assetType: createdAsset.assetType,
          assetUrl: createdAsset.assetUrl,
          eventId: createdAsset.eventId,
          thumbnailUrl: createdAsset.thumbnailUrl,
          tags: createdAsset.tags,
          userId: createdAsset.userId
        },
        order: prevAssets.length + 1,
        durationMultiplier: 1
      } as TributeVideoAsset;
      return [...prevAssets, newAsset];
    });
  };

  const searchCallback = (query: string) => {
    setSearch(query);
  };

  const handleUpdateDurationMultiplier = (assetId: number, newValue: number) => {
    const updatedArray = selectedPhotos.map((photo) =>
      photo.asset.id === assetId ? { ...photo, durationMultiplier: newValue } : photo
    );

    setSelectedPhotos(updatedArray);
  };

  return (
    <>
      {isLoading && (
        <div className='flex min-h-[60vh] items-center justify-center'>
          <Spinner />
        </div>
      )}
      {!isLoading && (
        <div className='flex size-full flex-col'>
          <div className='flex flex-col'>
            <Label text={t('photosAndVideos')} type={LABEL_TYPE.H2} />
            {maxAssets && (
              <Label
                text={t('photosMinAndMax', {
                  maxAssets
                })}
                type={LABEL_TYPE.PARAGRAPH1}
                className='mt-6 md:!font-semibold'
              />
            )}
          </div>
          <div className='mt-9 rounded-lg bg-primary-gray'>
            <div className='flex flex-col justify-between px-4 pt-5 md:flex-row md:items-center md:px-9 md:pt-9'>
              <Label
                text={t('uploadedPhotosAndVideos')}
                type={LABEL_TYPE.H4_BOLD}
                className='mb-4 md:mb-0'
              />
              <div className='flex flex-row gap-2'>
                <Button
                  variant={ButtonVariant.SECONDARY}
                  handleOnClick={() => {
                    if (!buttonsAreDisabled) {
                      setIsStockLibrary(true);
                      setIsUploadedPhotosModalOpen(true);
                    }
                  }}
                  className='gap-2'
                  disabled={buttonsAreDisabled}>
                  <PhotoFilmIcon className='size-4' />
                  {t('stockClips')}
                </Button>
                <UploadButton
                  disabled={buttonsAreDisabled || isUploadingAssets}
                  multiple
                  text='uploadPhotosAndVideos'
                  pickerTypes={UPLOAD_PICKER_TYPES.IMAGE_VIDEO}
                  icon={<ImageIcon className={`mr-2 ${buttonsAreDisabled ? 'opacity-80' : ''}`} />}
                  className={buttonsAreDisabled ? 'opacity-80' : ''}
                  showMaxFileSize={false}
                  isUploading={isUploadingAssets}
                  onFilesSelected={uploadFiles}
                />
              </div>
            </div>
            {hasAnyVisualAssets && isAllowedTo([PERMISSION_TYPE.ORG_ACCESS]) && (
              <>
                {!hasNoPhotosOrTextOverlays && (
                  <Label
                    text={t('photosDuration', {
                      assetDuration: durationOfSingleImage ?? durationOfAssets
                    })}
                    className={`px-4 !text-sm md:px-9 ${invalidAveragePhotoDuration ? '!text-accent-red' : ''}`}
                  />
                )}
                {durationOfSingleImage === undefined && videosDurationForDisplay > 0 && (
                  <Label
                    text={t('tributeVideoDurationSummary', {
                      availableDuration: availableDurationForDisplay,
                      videosDuration: videosDurationForDisplay
                    })}
                    className={`px-4 !text-sm md:px-9 ${invalidTotalVideoDuration ? '!text-accent-red' : ''}`}
                  />
                )}
              </>
            )}
            <div
              className={`relative grid grid-cols-2 items-center justify-between gap-20 p-2 pb-5 md:grid-cols-5 md:gap-0 md:p-7 ${
                dragging && 'size-full border-2 border-dashed border-blue-500 bg-blue-100'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleFileDrop}>
              {dragging && (
                <div className='absolute inset-0 z-10 flex items-center justify-center bg-white/40 backdrop-blur-md'>
                  <Label
                    className='text-xl font-semibold'
                    type={LABEL_TYPE.H1}
                    text={t('dropHere')}
                  />
                </div>
              )}
              {showPopup && (
                <DroppedAssetPopup
                  files={droppedFiles}
                  isPublic={false}
                  onClose={handleCancel}
                  eventId={Number(id)}
                  handleCreatedAsset={handleCreatedAsset}
                  pickerTypes={UPLOAD_PICKER_TYPES.IMAGE_VIDEO}
                  maxAssets={maxAssets ? maxAssets - selectedPhotos.length : undefined}
                  isMaxAssetsSizeLimited={true}
                />
              )}
              {selectedPhotos.length !== 0 &&
                selectedPhotos
                  ?.sort((a, b) => {
                    return a.order - b.order;
                  })
                  .map((asset: TributeVideoAsset, index) => (
                    <AssetItem
                      asset={asset.asset}
                      key={index}
                      order={
                        selectedPhotos?.filter((photo) => photo.asset.id === asset.asset.id)[0]
                          ?.order
                      }
                      handleUpdateAssetOrder={handleUpdateAssetOrder}
                      deleteSelectedAsset={(asset: Asset) => {
                        setSelectedPhotos(selectedPhotos?.filter((photo) => photo.asset !== asset));
                      }}
                      isDraggable
                      canBeRemoved
                      tributeVideoAsset={asset}
                      showDurationMultiplier={
                        asset.asset.assetType === IMAGE || asset.asset.assetType === TEXT_OVERLAY
                      }
                      onChangeDurationMultiplier={(newValue) =>
                        handleUpdateDurationMultiplier(asset.id, newValue)
                      }
                      selectedVideoUrl={''}
                      canBeSelected
                      canBeEdited={
                        asset.asset.assetType === IMAGE || asset.asset.assetType === TEXT_OVERLAY
                      }
                      isTributeVideoAssetsPage
                    />
                  ))}
              <div className='size-full pb-4 pr-3 pt-3'>
                <div
                  data-testid={TestIds.tributeVideo.browseUploadedPhotosButton}
                  className={`ml-1 flex aspect-square w-full cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-primary-blue px-4 underline underline-offset-1 ${
                    buttonsAreDisabled ? '!text-secondary-gray' : ''
                  } `}
                  onClick={() => {
                    if (!buttonsAreDisabled) {
                      setIsStockLibrary(false);
                      setIsUploadedPhotosModalOpen(true);
                    }
                  }}>
                  <ImageIcon
                    className={`mb-2 size-6 md:mb-4 ${buttonsAreDisabled ? 'opacity-70' : ''}`}
                  />
                  <p
                    className={`flex flex-row text-center font-bold text-primary-blue ${
                      buttonsAreDisabled ? '!text-secondary-gray' : ''
                    }`}>
                    {t('browseUploadedPhotosAndVideos')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {isUploadedPhotosModalOpen && (
        <Modal
          isOpen={true}
          height='h-full lg:h-[550px]'
          label={isStockLibrary ? 'stockClips' : 'alreadyUsedPhotosAndVideos'}
          description={
            maximumPhotosSelected
              ? t('maximumPhotosSelected')
              : isStockLibrary
                ? t('pleaseSelectStock')
                : t('pleaseSelectPhotos')
          }
          descriptionClasses={`!mb-2 ${maximumPhotosSelected ? '!text-accent-red' : ''}`}
          onClose={() => {
            setIsUploadedPhotosModalOpen(false);
            setSelectedAssets([]);
            setMaximumPhotosSelected(false);
            setLastClickedAsset(null);
          }}
          modalClasses='md:w-[700px] border border-secondary-blue-gray'
          hasButton
          buttonText={t('confirm')}
          isButtonDisabled={maximumPhotosSelected}
          buttonClasses='flex justify-end'
          buttonType={ButtonVariant.SECONDARY}
          handleSubmit={onSubmitSelection}
          isModalWithSearchAndFilters={true}>
          {!isStockLibrary && (
            <div className='mb-6 mt-2 flex flex-row justify-start gap-x-2'>
              <SingleSelect
                className='w-36'
                label=''
                isClearable
                id='assetFilter'
                placeholder={t('filters')}
                options={getPhotoAssetFilterOptions()}
                handleChange={(value) => {
                  setFilterByType(value as SelectOptionProps);
                }}
                value={filterByType}
                textColor={COLORS.PRIMARY_BLUE}
                borderStyle={`1px solid ${COLORS.PRIMARY_BLUE}`}
                fontWeight='!font-bold'
                dropdownIndicatorColor={COLORS.PRIMARY_BLUE}
              />
              <AssetsTagFilter
                setSelectedTags={setSelectedTags}
                selectedTags={selectedTags}
                eventId={id}
              />
            </div>
          )}
          {isStockLibrary && (
            <div className='mb-4 mt-2 flex flex-col justify-start gap-x-2'>
              <Search callback={searchCallback} />
              {hasRecommendedStockClips && (
                <div className='mt-5 flex w-full flex-row items-center justify-start'>
                  <div
                    className={`flex w-1/2 cursor-pointer flex-row items-center justify-center border-b-2 pb-2 ${showRecommendedVideos ? 'border-primary-blue font-semibold text-primary-blue' : 'text-secondary-gray'}`}
                    onClick={() => setShowRecommendedVideos(true)}>
                    {t('recommendedVideos')}
                  </div>
                  <div
                    className={`flex w-1/2 cursor-pointer flex-row items-center justify-center border-b-2 pb-2 ${showRecommendedVideos ? 'text-secondary-gray' : 'border-primary-blue font-semibold text-primary-blue'}`}
                    onClick={() => setShowRecommendedVideos(false)}>
                    {t('allExistingVideos')}
                  </div>
                </div>
              )}
            </div>
          )}
          <div>
            {selectedTags && (
              <div className='mb-3 grid w-full items-center justify-start gap-y-2 md:grid-cols-5'>
                {selectedTags.map((tag) => (
                  <div className='w-full' key={tag?.value as number}>
                    <AssetTagItem
                      tag={tag}
                      handleTagRemove={handleTagRemove}
                      key={tag?.value as number}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          <div
            data-testid={TestIds.tributeVideo.uploadedAssetsModalScroll}
            className='custom-scrollbar flex-1 overflow-y-auto'>
            <div
              data-testid={TestIds.tributeVideo.uploadedAssetsModalGrid}
              className={`grid grid-cols-3 items-center justify-between gap-2 pr-2 md:grid-cols-5 md:gap-4 ${!isStockLibrary && 'md:pt-4'}`}>
              {!isLoading && (
                <>
                  {(isStockLibrary
                    ? showRecommendedVideos && hasRecommendedStockClips
                      ? recommendedStockClipsData
                      : publicAssetsData
                    : allAssets
                  )?.pages.map((page, pageIndex) =>
                    (isStockLibrary
                      ? page.data?.items.map(
                          (item: { stockClipAsset: StockClipAsset }, idx: number) => ({
                            ...(item.stockClipAsset ?? item),
                            originalIndex: globalIndex(pageIndex, idx)
                          })
                        )
                      : [...(page?.data?.items || [])]
                          .map((item: Asset, idx: number) => ({
                            ...item,
                            originalIndex: globalIndex(pageIndex, idx)
                          }))
                          .sort((a: Asset, b: Asset) => a.id - b.id)
                    )?.map((asset: Asset, index: number) => (
                      <div
                        data-testid={getTestId(
                          TestIds.tributeVideo.uploadedAssetItem,
                          asset.assetName
                        )}
                        onClick={(e) => handleClickAsset(asset, e, globalIndex(pageIndex, index))}
                        key={asset.id}>
                        <AssetItem
                          setSelectedAsset={() => {
                            setSelectedAsset;
                          }}
                          selectedAssets={selectedAssets}
                          asset={asset}
                          key={asset.id}
                          selectedAsset={selectedAsset as Asset}
                          canBeDeleted={false}
                          canBeEdited={false}
                          multiselect
                          selectedVideoUrl={''}
                          canBeSelected
                        />
                      </div>
                    ))
                  )}
                </>
              )}
            </div>
            <div ref={ref} className='h-1 w-full'></div>
            {!isLoading && (
              <PaginationArrow
                isFetchingNextPage={isFetchingNextPage || isPublicAssetsFetchingNextPage}
                inView={inView}
              />
            )}
          </div>
        </Modal>
      )}
    </>
  );
};
